// The home page (`/`).
//
// The page's chrome is the site's own - the nav, the footer, the language
// switcher, the cookie notice and the demo form all behave exactly as they do on
// every other page - so this module runs `main.ts` rather than restating it.
// What is its own is the stylesheet and two small behaviours: the hours
// calculator beside the headline, and the one task a reader can answer.
//
// NO STRING IS WRITTEN HERE. Every word the page can show is already in the
// document, marked with the catalog key that owns it; the verdicts of the task
// are hidden elements this file reveals. The calculator's readings are numbers
// and units, and those come from `Intl` in the document's own language, so a
// Ukrainian page reads "8,7 год" and a Polish one "8,7 godz." with nothing
// translated here.
// `main` first: its stylesheet is the site's, and the home page's own rules come
// after it so that they win where both style one element (the hero).
import './main';
import './home.css';

// --- The hours calculator -----------------------------------------------------
// It counts what the platform certainly takes off a teacher: the closed share of
// the homework they mark. Preparation is left out on purpose, and the page says
// so under the sliders - a figure the product cannot stand behind is not one the
// page should print.
//
// The arithmetic is in WHOLE units of the reader's own currency, never cents:
// the inputs are integers, the product of them is an integer, and one division
// at the end rounds it. The figure is an estimate of somebody's time, not an
// amount the platform holds, but it is still never carried as a fraction.

/** A slider's range, as the attributes it takes: min, max, step, start. */
type Range = readonly [min: number, max: number, step: number, start: number];

/** The hourly figure a reader is asked for, in the currency their language's
 *  market is paid in, rounded to a step a person would quote. A language not
 *  listed is asked in euro, the currency of the price list. */
const HOURLY: Readonly<Record<string, { currency: string; range: Range; step: number }>> = {
  uk: { currency: 'UAH', range: [200, 1500, 50, 500], step: 10 },
  pl: { currency: 'PLN', range: [40, 300, 5, 100], step: 10 },
};
const HOURLY_DEFAULT = { currency: 'EUR', range: [10, 80, 1, 25] as Range, step: 1 };

/** Weeks in a year and months in it: a month is a twelfth of 52 weeks, which is
 *  what the fine print under the sliders says. */
const WEEKS_PER_YEAR = 52;
const MONTHS_PER_YEAR = 12;
const MINUTES_PER_HOUR = 60;
const PERCENT = 100;

function calculator(root: HTMLElement): void {
  const lang = document.documentElement.lang || 'en';
  const market = HOURLY[lang.split('-')[0]!] ?? HOURLY_DEFAULT;

  const input = (id: string) => root.querySelector<HTMLInputElement>(`#${id}`);
  const output = (id: string) => root.querySelector<HTMLOutputElement>(`#${id}-out`);
  const reading = (name: string) => root.querySelector<HTMLElement>(`[data-out="${name}"]`);

  const students = input('hm-students');
  const minutes = input('hm-minutes');
  const share = input('hm-share');
  const hourly = input('hm-hourly');
  if (!students || !minutes || !share || !hourly) return;

  // The language's own range. The markup carries the euro one, because a
  // document is written once and this is the one place that knows the market.
  const [min, max, step, start] = market.range;
  Object.assign(hourly, { min: String(min), max: String(max), step: String(step) });
  hourly.value = String(start);

  // Built here, after the language is known, and never at module scope: a
  // formatter built at import would keep the language it was born with.
  const count = new Intl.NumberFormat(lang);
  const percent = new Intl.NumberFormat(lang, { style: 'percent' });
  const minuteUnit = new Intl.NumberFormat(lang, { style: 'unit', unit: 'minute', unitDisplay: 'short' });
  const hourUnit = (digits: number) =>
    new Intl.NumberFormat(lang, { style: 'unit', unit: 'hour', unitDisplay: 'short', maximumFractionDigits: digits, minimumFractionDigits: digits });
  const money = new Intl.NumberFormat(lang, { style: 'currency', currency: market.currency, maximumFractionDigits: 0 });
  // A tenth of an hour while the figure is small, none once it is not - and none
  // at zero, which reads "0 hr", never "0.0 hr".
  const hours = (value: number) => hourUnit(value > 0 && value < 10 ? 1 : 0).format(value);

  const render = () => {
    const perWeek = Number(students.value) * Number(minutes.value);
    const closed = Number(share.value);
    const perHour = Number(hourly.value);

    // Minutes a month = perWeek * 52 / 12; hours = that / 60.
    const monthHours = (perWeek * WEEKS_PER_YEAR) / (MONTHS_PER_YEAR * MINUTES_PER_HOUR);
    const backHours = (monthHours * closed) / PERCENT;
    // The worth, in whole units: one integer numerator, one division, rounded
    // to the step a person would say out loud.
    const numerator = perWeek * WEEKS_PER_YEAR * closed * perHour;
    const denominator = MONTHS_PER_YEAR * MINUTES_PER_HOUR * PERCENT * market.step;
    const worth = Math.round(numerator / denominator) * market.step;

    const set = (el: Element | null, text: string) => {
      if (el && el.textContent !== text) el.textContent = text;
    };
    set(output('hm-students'), count.format(Number(students.value)));
    set(output('hm-minutes'), minuteUnit.format(Number(minutes.value)));
    set(output('hm-share'), percent.format(closed / PERCENT));
    set(output('hm-hourly'), money.format(perHour));
    set(reading('hours'), hours(monthHours));
    set(reading('back'), hours(backHours));
    set(reading('worth'), money.format(worth));
  };

  for (const control of [students, minutes, share, hourly]) control.addEventListener('input', render);
  render();
}

// --- The task ------------------------------------------------------------------
// One question, marked the moment it is answered, the way a task is in a lesson.
// A wrong answer shows the right one beside it, as the student's own screen does.
function task(root: HTMLElement): void {
  const choices = [...root.querySelectorAll<HTMLButtonElement>('.hm-choice')];
  const gap = root.querySelector<HTMLElement>('#hm-gap');
  const verdicts = [...root.querySelectorAll<HTMLElement>('[data-verdict]')];
  if (!gap || choices.length === 0) return;
  const blank = gap.textContent ?? '';

  for (const choice of choices) {
    choice.addEventListener('click', () => {
      const right = choice.dataset.ok === 'true';
      for (const other of choices) {
        other.removeAttribute('data-state');
        other.setAttribute('aria-pressed', 'false');
      }
      choice.setAttribute('aria-pressed', 'true');
      choice.dataset.state = right ? 'right' : 'wrong';
      if (!right) {
        const answer = choices.find((other) => other.dataset.ok === 'true');
        if (answer) answer.dataset.state = 'right';
      }
      gap.textContent = choice.textContent ?? blank;
      gap.dataset.state = right ? 'right' : 'wrong';
      for (const verdict of verdicts) verdict.hidden = verdict.dataset.verdict !== (right ? 'right' : 'wrong');
    });
  }
}

const calc = document.querySelector<HTMLElement>('#hm-calc');
if (calc) calculator(calc);
const taskRoot = document.querySelector<HTMLElement>('#hm-task');
if (taskRoot) task(taskRoot);
